import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Table, Button, message } from 'antd'
import { createUseStyles } from 'react-jss'
import TextArea from 'antd/lib/input/TextArea'
import { PageHeader } from '../Common'
import { useAsync } from '../../hooks'
import {
  usePracticeFormOption,
  addPracticeFormOption,
  usePracticeFormOptionPagination,
  deletePracticeFormOption,
  updatePracticeFormOption,
  useFetchPracticeFormOption,
} from '../../redux/slices/practice-form-options'
import useQueryParams from '../../hooks/useQueryParams'
import api from '../../api'
import { usePhrasesColumns } from './useColumns'

const useStyles = createUseStyles({
  container: {
    width: '70%',
    margin: '0 auto',
    marginBottom: '1rem',
  },
  center: {
    textAlign: 'center',
  },
  newPhraseContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: 'solid 1px #e8e8e8',
  },
  newPhrase: {
    width: '100%',
    height: '100px',
    margin: '1rem',
  },
  rows: {
    '& td:last-child': {
      width: '5%',
      textAlign: 'center',
    },
  },
})

const QUERY_PARAMS_SCHEMA = {
  page: Number,
  practiceId: String,
}

export default function PracticePresetPhrases() {
  const { practiceId } = useParams()
  const classes = useStyles()
  const history = useHistory()

  const [newPhrase, setNewPhrase] = useState('')
  const [editingPhraseId, setEditingPhraseId] = useState(null)
  const [editedPhraseValue, setEditedPhraseValue] = useState('')
  const [loading, setLoading] = useState('none')

  const getPractice = useCallback(
    () =>
      api.practices.getPractice(practiceId).then(practice => {
        practice.logo = practice.logoPath
        return practice
      }),
    [practiceId],
  )

  const { value: practice } = useAsync(getPractice)

  const [conditions, setConditions] = useQueryParams({
    initialValues: { page: 1, practiceId },
    schema: QUERY_PARAMS_SCHEMA,
  })

  const practiceFormOption = usePracticeFormOption(practiceId)
  const pagination = usePracticeFormOptionPagination()

  const fetchPracticeFormOption = useFetchPracticeFormOption(
    practiceId,
    conditions.page,
    10,
  )

  useEffect(() => {
    setLoading('fetch')
    fetchPracticeFormOption()
    setLoading('none')
  }, [conditions.page, fetchPracticeFormOption])

  const handlePageChange = useCallback(
    page => {
      if (page !== conditions.page) {
        setConditions({ ...conditions, page })
      }
    },
    [conditions, setConditions],
  )

  const handleAddPhrase = async () => {
    if (!newPhrase.trim()) return
    setLoading('create')
    try {
      await addPracticeFormOption({
        practice_form_option: {
          value: newPhrase,
          practiceId,
        },
        default_report_names: true,
      })
      await fetchPracticeFormOption()
      message.success('Phrase added successfully!')
    } catch (error) {
      message.error('Failed to add phrase')
    } finally {
      setLoading('none')
      setNewPhrase('')
    }
  }

  const handleDeletePhrase = async phraseId => {
    setLoading('delete')
    try {
      await deletePracticeFormOption(phraseId)
      await fetchPracticeFormOption()
      message.success('Phrase deleted successfully!')
    } catch (error) {
      message.error('Failed to delete phrase')
    } finally {
      setLoading('none')
    }
  }

  const handleEditPhrase = (phraseId, currentValue) => {
    setEditingPhraseId(phraseId)
    setEditedPhraseValue(currentValue)
  }

  const handleConfirmEdit = async phraseId => {
    setLoading('update')
    try {
      await updatePracticeFormOption(phraseId, {
        value: editedPhraseValue,
      })
      await fetchPracticeFormOption()
      message.success('Phrase updated successfully!')
      setEditingPhraseId(null)
      setEditedPhraseValue('')
    } catch (error) {
      message.error('Failed to update phrase')
    } finally {
      setLoading('none')
    }
  }

  const columns = usePhrasesColumns(
    editingPhraseId,
    editedPhraseValue,
    setEditedPhraseValue,
    handleEditPhrase,
    handleConfirmEdit,
    handleDeletePhrase,
    loading,
  )

  return (
    <div>
      <PageHeader
        title={`Preset Phrases - Practice ${practice?.name}`}
        onBack={history.goBack}
      />
      <div className={classes.container}>
        <div className={classes.newPhraseContainer}>
          <TextArea
            className={classes.newPhrase}
            placeholder="Preset phrase"
            value={newPhrase}
            onChange={e => setNewPhrase(e.target.value)}
          />
          <Button
            type="primary"
            disabled={!newPhrase.trim()}
            onClick={handleAddPhrase}
            loading={loading === 'create'}
          >
            Add New Phrase
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={practiceFormOption}
          loading={loading === 'fetch'}
          rowClassName={classes.rows}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.perPage || 10,
            onChange: handlePageChange,
            total: pagination.totalCount || 0,
          }}
          rowKey={record => `${record.id}-${record.value}-${record.key}`}
        />
        <div className={classes.center}>
          <Button type="primary" onClick={() => history.goBack()}>
            Done
          </Button>
        </div>
      </div>
    </div>
  )
}
